import {
	CONSOLIDATOR_ID_MVP,
	CONSOLIDATOR_ID_VETCORE,
	PAYMENT_INTENT_DEFAULT_POLLING_INTERVAL_MS,
	PAYMENT_INTENT_DEFAULT_TIMEOUT_MS
} from '../constants'
import { validatePaymentIntentPollingInterval, validatePaymentIntentTimeout } from './util'

export const shouldHideAccountsReceivable = (currentConsolidator: IConsolidator | undefined) =>
	currentConsolidator?.featureFlags?.hideAccountsReceivable ?? false

// Consolidator-level opt-out for the referral program entry point. Suppresses it for every
// practice under the consolidator, overriding the per-practice flag (DASH-8713).
export const shouldHideReferralProgram = (consolidator: IConsolidator | undefined) =>
	consolidator?.featureFlags?.hideReferralProgram ?? false

export const shouldHideInviteUserWithEmailButton = (consolidator?: IConsolidator) =>
	consolidator?.featureFlags?.hideInviteUserWithEmail === true

// By default show by card type and hide by hideTotalsByCardType mostly for VetCor
export const shouldShowTotalsByCardType = (currentConsolidator: IConsolidator | undefined) => {
	if (currentConsolidator?.featureFlags?.hideTotalsByCardType) {
		return false
	}
	return true
}

export const shouldShowManualPaymentTabToggle = (currentConsolidator: IConsolidator | undefined) =>
	currentConsolidator?.featureFlags?.allowDisablingPhonePayments ?? false

export const shouldShowOpenActionItems = (currentConsolidator: IConsolidator | undefined) =>
	currentConsolidator?.featureFlags?.showOpenActionItems ?? false

export const shouldRequireInvoiceNumber = (currentConsolidator: IConsolidator | undefined) =>
	currentConsolidator?.featureFlags?.requireInvoiceNumber ?? false

export const shouldDisplayIndustryProgramsForConsolidator = (currentConsolidator?: IConsolidator) =>
	getSettingByName(currentConsolidator, 'isIndustryPromoEnabled')

export const shouldShowAllRecurringPayments = (currentConsolidator?: IConsolidator) =>
	getSettingByName(currentConsolidator, 'showAdminAllRecurringPayments')

export const shouldRestrictRefundsForAdmins = (currentConsolidator?: IConsolidator) =>
	currentConsolidator?.featureFlags?.restrictRefundsToAdmins

export const isDonationEnabled = (currentConsolidator?: IConsolidator) =>
	!!currentConsolidator?.featureFlags?.donationsEnabled

export const isTerminalTippingEnabled = (currentConsolidator?: IConsolidator) =>
	!!currentConsolidator?.featureFlags?.enableTerminalTipping

export const getTerminalTippingPromptByDefault = (currentConsolidator?: IConsolidator) =>
	!!currentConsolidator?.featureFlags?.terminalTippingConfig?.promptByDefault

export const getSettingByName = (
	consolidator: IConsolidator | undefined,
	settingName: string,
	defaultValue: boolean = false
) => consolidator?.featureFlags?.[settingName] ?? defaultValue

// We have some settings that may or may not have the "page".  Remove this when we are all on unified.
export const getSettingByNameAddPage = (
	consolidator: IConsolidator | undefined,
	settingName: string
) =>
	consolidator?.featureFlags?.[settingName] ??
	consolidator?.featureFlags?.[`${settingName}Page`] ??
	false

/**
 * Checks whether a specific feature is enabled based on the provided setting name and consolidator object.
 * Returns false ONLY if the settingName is explicitly defined in the consolidator settings object with a value of false.
 * Returns true if the settingName is not defined in the consolidator settings object or if its value is truthy.
 */
export const isFeatureEnabled = (consolidator, settingName) =>
	(consolidator?.featureFlags?.[settingName] ?? true) &&
	consolidator?.featureFlags?.[settingName] !== false

export const shouldShowConsolidatorPaymentHistory = (consolidator?: IConsolidator) =>
	getSettingByName(consolidator, 'showConsolidatorPaymentHistory', true)

export const shouldUseAdminLocationsV2 = (consolidator?: IConsolidator) =>
	getSettingByName(consolidator, 'useAdminLocationsV2')

export const getAdminSectionProps = (
	consolidator: IConsolidator | undefined,
	openActionItemsCount: number
): Omit<
	AdminSectionProps,
	'showEnterpriseRebateHistory' | 'showRebatePayoutsPage' | 'showAllCardRequestsPage'
> => ({
	showCheckReportingPage: getSettingByNameAddPage(consolidator, 'showCheckReporting'),
	showAllDisputesPage: getSettingByNameAddPage(consolidator, 'showAllDisputes'),
	showVendorNamesPage: getSettingByNameAddPage(consolidator, 'showVendorNames'),
	showOpenActionItemsPage: getSettingByNameAddPage(consolidator, 'showOpenActionItems'),
	openActionItemsCount,
	showHelpDeskPage: getSettingByNameAddPage(consolidator, 'showHelpDesk'),
	showPostcardPage: getSettingByNameAddPage(consolidator, 'showPostcard'),
	showLockboxChecksPage: getSettingByNameAddPage(consolidator, 'showLockboxChecks'),
	showDailyTransactionFeed: getSettingByName(consolidator, 'showDailyTransactionFeed'),
	showCheckReportingV2: getSettingByName(consolidator, 'useCheckReportingV2'),
	showConsolidatorPaymentHistory: shouldShowConsolidatorPaymentHistory(consolidator),
	showConsolidatorMonthlyReport: getSettingByName(consolidator, 'showConsolidatorMonthlyReport'),
	showAllRecurringPayments: shouldShowAllRecurringPayments(consolidator),
	showDailyCashCountPage: showDailyCashCountPage(consolidator),
	useAdminLocationsV2: shouldUseAdminLocationsV2(consolidator)
	// `showEnterpriseRebateHistory` and `showRebatePayoutsPage` are resolved at render time
	// via `useConsolidatorFeatureFlag` so each page can be toggled independently per consolidator.
})

export const shouldShowStoredCardsReport = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'isSavePaymentMethodsEnabled') === true ||
	getSettingByName(consolidator, 'isStoredCardsReportEnabled') === true

export const shouldAllowStoringCardsOnlinePayment = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'isSavePaymentMethodsEnabled') === true

export const isStoredCardsReportEnabled = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'isStoredCardsReportEnabled') === true

// Consolidator-level fallbacks for the recurring-payment card flags. Used only
// when the practice hasn't set the flag explicitly; undefined/false → false.
export const isTerminalSaveOnlyEnabled = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'terminalSaveOnlyEnabled') === true

export const isRecurringSavedCardOnlyEnabled = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'recurringSavedCardOnlyEnabled') === true

// Consolidator-level fallback for the card request flag, used only when the practice
// hasn't set it explicitly. Off by default.
export const isSaveCardRequestEnabled = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'saveCardRequestEnabled') === true

// Consolidator-level fallback for saved-card-only mode, used only when the practice
// hasn't set savedCardOnly explicitly. Off by default.
export const isSavedCardOnlyEnabled = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'savedCardOnly') === true

export const isSavedPaymentMethodsV2Enabled = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'useSavedPaymentMethodsV2') === true

export const shouldShowCheckPayments = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'isCheckEnabled') === true

export const shouldShowTerminalPayments = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'isTerminalEnabled', true)

export const shouldShowConsolidatorMonthlyReports = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'showConsolidatorMonthlyReport') === true

export const shouldHideFeeCheckReceipts = (currentConsolidator: IConsolidator | undefined) =>
	currentConsolidator?.featureFlags?.hideFeeCheckReceipts ?? false

export const getValidateClientIdSettings = (
	consolidator: IConsolidator | undefined
): ValidateClientIdSettings | undefined => {
	const validateClientIdSettings = consolidator?.featureFlags?.payment?.validateClientIdSettings
	if (validateClientIdSettings?.enabled) {
		return validateClientIdSettings
	}
	return undefined
}

// Despite the name, this flag also removes lockbox options: it makes the flow PIMS-only.
export const shouldShowLockBoxMVPAdditions = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'lockboxMVPadditions')

export const shouldValidateClientId = (consolidator: IConsolidator | undefined): boolean =>
	Boolean(consolidator?.featureFlags?.payment?.validateClientIdSettings?.enabled)

export const validateInvoiceIsNumber = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'validateInvoiceIsNumber')

export const shouldShowNewLockboxAddress = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'showNewLockboxAddress')

export const shouldHideReceiptModal = (consolidator?: IConsolidator) =>
	consolidator?.featureFlags?.disablePaymentReceiptModal

export const consolidatorShouldShowDailyTotalsCardGrouping = (
	consolidator: IConsolidator | undefined
) => consolidator?.featureFlags?.showDailyTotalsCardGrouping ?? false

export const shouldShowPimsFields = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'showPaymentHistoryPimsFields')

export const shouldShowRefundRequest = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'refundRequest')?.enabled ?? false

export const shouldShowRegionalManager = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'showRegionalManager')

export const shouldValidateBankAccount = (
	userConsolidatorId: string | null,
	consolidatorRoutingNumber?: string
) =>
	// TODO: remove hardcoded checks
	userConsolidatorId !== CONSOLIDATOR_ID_VETCORE &&
	userConsolidatorId !== CONSOLIDATOR_ID_MVP &&
	!consolidatorRoutingNumber

export const shouldUseSupportedCountries = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'supportedCountries')

export const isPaymentRequestEnabled = (consolidator: IConsolidator | undefined) =>
	isFeatureEnabled(consolidator, 'isPaymentRequestEnabled')

export const shouldHideReconciliation = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'hideReconciliation')

export const shouldHidePayoutsPage = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'hidePayouts')

export const shouldShowMonthlyStatementsPage = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'showMonthlyStatements')

export const blockAmexPayments = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'blockAmex')

export const showPostcardPage = (consolidator: IConsolidator | undefined) =>
	getSettingByNameAddPage(consolidator, 'showPostcard')

export const isSurchargeRecurringPaymentsEnabled = (consolidator: IConsolidator | undefined) =>
	consolidator?.featureFlags?.surchargeRecurringPayments

export const isInsurancePaymentBypassEnabled = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'insurancePaymentBypassEnabled')

/** Returns true if the consolidator has default surcharge configured for new locations. */
export const hasDefaultSurcharge = (consolidator: IConsolidator | undefined) =>
	!!consolidator?.featureFlags?.defaultSurchargeEnabled

export const isStoredCardsV2Enabled = (consolidator?: IConsolidator) =>
	getSettingByName(consolidator, 'storedCardsV2Enabled')

export const showDailyCashCountPage = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'dailyCashCount')

export const shouldShowSavePaymentMethodCheckbox = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'terminalSavedCardsEnabled')

export const getConsolidatorPaymentIntentPollingIntervalMs = (
	consolidator: IConsolidator | undefined
): number => {
	const intervalMs =
		consolidator?.featureFlags?.paymentIntentPollingIntervalMs ??
		PAYMENT_INTENT_DEFAULT_POLLING_INTERVAL_MS
	return validatePaymentIntentPollingInterval(intervalMs)
}

export const getConsolidatorPaymentIntentPollingTimeoutMs = (
	consolidator: IConsolidator | undefined
): number => {
	const timeoutMs =
		consolidator?.featureFlags?.paymentIntentPollingTimeoutMs ?? PAYMENT_INTENT_DEFAULT_TIMEOUT_MS
	return validatePaymentIntentTimeout(timeoutMs)
}

export const shouldShowSavePaymentMethodDisclousure = (consolidator: IConsolidator | undefined) =>
	getSettingByName(consolidator, 'terminalSavedCardsDisclosureEnabled')
